import {
	StyledFormControl,
	TextFieldLabel,
} from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import {
	Autocomplete as MuiAutocomplete,
	AutocompleteProps as MuiAutocompleteProps,
	TextField,
	TextFieldProps,
} from '@mui/material';
import { ControllerFieldState } from 'react-hook-form';

type AutocompleteProps<
	VALUE = unknown,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean | undefined = undefined
> = Omit<
	Partial<MuiAutocompleteProps<VALUE, Multiple, DisableClearable, FreeSolo>>,
	'value' | 'options' | 'onChange'
> & {
	options?: ({ id: string | number; label: string } & VALUE)[];
	value?: string | number | null;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	label?: string;
	optionKey?: string;
	textFieldProps?: TextFieldProps;
	showLabel?: boolean;
	required?: boolean;
	fieldState?: ControllerFieldState;
};

/**
 * Search must match how people actually type Arabic: hamza carriers, taa
 * marbuta and alef maqsura collapse to their bare forms, and diacritics
 * are ignored — otherwise typing "الاول" misses "الأول" that is
 * visibly in the list.
 *
 * The same folding runs over the query AND the option label, so anything NOT
 * folded here is a way for a visibly-matching row to become unfindable. That is
 * why invisible marks, NBSP, Arabic-Indic digits and the whole family of dashes
 * are normalised too, rather than left to chance.
 */
const normalizeSearchText = (text: string) =>
	text
		.toLowerCase()
		// Invisible characters first: bidi marks and zero-width joiners ride along
		// in titles pasted from Word or WhatsApp, and would otherwise break an
		// exact-looking match with nothing on screen to explain it.
		.replace(/[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, '')
		.replace(/\u00A0/g, ' ') // NBSP is typed as an ordinary space
		.replace(/[\u064B-\u065F\u0670]/g, '') // harakat/tanween + superscript alef
		// Arabic-Indic and Persian digits fold to ASCII, so "1" finds "١".
		.replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
		.replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
		// Every dash-shaped character folds to "-", tatweel included: it is
		// routinely used as a visual separator in Arabic titles, and deleting it
		// (as this line used to) meant a typed "-" could never match such a title.
		.replace(/[\u0640\u2010-\u2015\u2212]/g, '-')
		.replace(/[أإآٱ]/g, 'ا')
		.replace(/ى/g, 'ي')
		.replace(/ئ/g, 'ي')
		.replace(/ؤ/g, 'و')
		.replace(/ة/g, 'ه')
		.replace(/\s+/g, ' '); // collapse runs so spacing differences stop mattering

export const Autocomplete = <
	VALUE = unknown,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean | undefined = undefined
>({
	onChange,
	optionKey = 'id',
	showLabel = false,
	fieldState,
	label,
	value,
	...props
}: AutocompleteProps<VALUE, Multiple, DisableClearable, FreeSolo>) => {
	// The form value is the raw id (may arrive as a string from the API), while
	// MUI needs the matching option object — and options load async, so the
	// selection must be re-derived whenever options or value change.
	const selectedOption =
		value !== null && value !== undefined
			? props.options?.find((option) => String(option.id) === String(value)) ??
			  null
			: null;

	return (
		<StyledFormControl>
			{showLabel && (
				<TextFieldLabel required={props.required}>{label}</TextFieldLabel>
			)}
			<MuiAutocomplete
				fullWidth
				renderInput={(params) => (
					<TextField
						sx={{ width: '16.25rem' }}
						{...props.textFieldProps}
						{...params}
						label={label}
						error={!!fieldState?.error || props.textFieldProps?.error}
						helperText={
							fieldState?.error?.message ?? props.textFieldProps?.helperText
						}
					/>
				)}
				{...props}
				options={(props.options || []) as VALUE[]}
				// The input text stays UNCONTROLLED (MUI-owned): the previous
				// controlled inputValue was fed by a raw DOM onChange and never
				// wired onInputChange, so MUI's replace/reset/clear paths were
				// dead — typed text stuck, retyping appended, and the invisible
				// stale filter rendered "No options" over populated lists.
				value={(selectedOption ?? null) as any}
				isOptionEqualToValue={(option, selected) =>
					String((option as any)?.[optionKey]) ===
					String((selected as any)?.[optionKey])
				}
				filterOptions={(options, state) => {
					const query = normalizeSearchText(state.inputValue.trim());
					if (!query) return options;
					return options.filter((option) =>
						normalizeSearchText(String((option as any)?.label ?? '')).includes(
							query
						)
					);
				}}
				getOptionKey={(option) => (option as any)[optionKey]}
				onChange={(_e, selected) => {
					// Callers consume `event.target.value` (the option id, null on
					// clear); a synthetic object keeps that contract without
					// mutating DOM nodes or re-dispatching native events.
					const syntheticEvent = {
						target: {
							value: selected ? (selected as any)[optionKey] : null,
						},
					} as unknown as React.ChangeEvent<HTMLInputElement>;
					onChange?.(syntheticEvent);
				}}
				getOptionLabel={(option) => (option as any).label ?? ''}
				filterSelectedOptions
				clearOnBlur
			/>
		</StyledFormControl>
	);
};
